import { rgba } from "polished"
import styled, { css, keyframes } from "styled-components"
import motionTokens from "styles/motionTokens"
import type { Theme } from "styles/theme"

import type { NotificationTone, ToneColorOverrides } from "../ToastStack/types"

export type ToastTone = NotificationTone

const TOAST_BACKGROUND_OPACITY = 0.12
const TOAST_BODY_OPACITY = 0.76

const getToneBackground = (
  theme: Theme,
  tone: ToastTone,
  toneColors?: ToneColorOverrides
) => toneColors?.[tone]?.background ?? theme.palette[tone].main

const getToneText = (
  theme: Theme,
  tone: ToastTone,
  toneColors?: ToneColorOverrides
) => toneColors?.[tone]?.text ?? theme.palette[tone].contrast

const slideIn = keyframes`
  from {
    transform: translateY(12px);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
`

export const ToastWrapper = styled.div<{
  $visible: boolean
  $reducedMotion?: boolean
  $tone: ToastTone
  $solidBackground: boolean
  $toneColors?: ToneColorOverrides
}>`
  background-color: ${({ theme, $tone, $solidBackground, $toneColors }) => {
    const toneBackground = getToneBackground(theme, $tone, $toneColors)
    return $solidBackground
      ? toneBackground
      : rgba(toneBackground, TOAST_BACKGROUND_OPACITY)
  }};
  color: ${({ theme, $tone, $solidBackground, $toneColors }) => {
    if ($toneColors?.[$tone]?.text) {
      return $toneColors[$tone]!.text
    }

    return $solidBackground
      ? getToneText(theme, $tone, $toneColors)
      : theme.palette.text.main
  }};
  border-radius: 12px;
  padding: 16px 20px;
  display: grid;
  gap: 12px;
  width: min(360px, calc(100vw - 32px));
  transition: ${({ $reducedMotion }) =>
    $reducedMotion
      ? "opacity 0ms linear"
      : `opacity ${motionTokens.duration.short}ms ${motionTokens.easing.standard},
    transform ${motionTokens.duration.short}ms ${motionTokens.easing.standard}`};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: ${({ $visible, $reducedMotion }) =>
    $reducedMotion || $visible ? "translateY(0)" : "translateY(12px)"};

  ${({ $reducedMotion }) =>
    $reducedMotion
      ? css`
          transform: translateY(0);
        `
      : css`
          animation: ${slideIn} ${motionTokens.duration.medium}ms
            ${motionTokens.easing.standard};
        `}

  border-left: 4px solid
    ${({ theme, $tone, $toneColors }) =>
    getToneBackground(theme, $tone, $toneColors)};
`

export const ToastHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  > :first-child {
    flex: 1;
    min-width: 0;
  }
`

export const ToastTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: inherit;
`

export const ToastBody = styled.div<{
  $solidBackground: boolean
  $tone: ToastTone
  $toneColors?: ToneColorOverrides
}>`
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme, $solidBackground, $tone, $toneColors }) => {
    const customText = $toneColors?.[$tone]?.text

    if ($solidBackground) {
      return customText ?? getToneText(theme, $tone, $toneColors)
    }

    if (customText) {
      return rgba(customText, TOAST_BODY_OPACITY)
    }

    return rgba(theme.palette.subtitle.main, TOAST_BODY_OPACITY)
  }};
`

export const ToastActions = styled.div`
  display: inline-flex;
  gap: 12px;
  flex-wrap: wrap;
`
